import * as React from 'react';

import {
  AssetStatus,
  enumAssetSortOption,
  enumAssetStatus,
} from '@rematter/frontend-lib-api-client';
import { useTranslations } from '@rematter/modules/transportation/i18n/useTranslations';
import { Button, LegacyMenu } from '@rematter/ui-web';
import {
  DataGrid,
  DataGridColumn,
  HeaderIcons,
  Pagination,
  type PaginationProps,
  Badge,
  BadgeProps,
} from '@rematter/ui-web';
import {
  Content,
  Footer,
  Toolbar,
  FloatingToolbar,
  Separator,
} from '@rematter/ui-web';
import { SearchField } from '@rematter/ui-web';
import { Loading } from '@rematter/ui-web';
import { useAtom } from 'jotai';
import { PlusCircle, Trash } from 'lucide-react';

import { useAssetsQuery } from '../../../hooks/queries/useAssetsQuery';
import { TransportationRootLayout } from '../../../layouts/TransportationRootLayout';
import { AssetRecord } from '../../../types';

import { AssetDialog } from './AssetDialog';

import {
  $currentSorting,
  $limit,
  $offset,
  $searchTerm,
  $selectedStatus,
} from '~/modules/transportation/_state/assetsList.state';
import { SelectEnum } from '~/shared/components/select-fields/SelectEnum';
import useDebouncedValue from '~/shared/hooks/useDebounce';
import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

export const AssetsListView: React.FC = () => {
  const t = useTranslations();
  const [, setLocation] = useModuleLocation();
  const [selectedRows, setSelectedRows] = React.useState<
    Partial<AssetRecord>[]
  >([]);
  const [searchTerm, setSearchTerm] = useAtom($searchTerm);
  const [selectedStatus, setSelectedStatus] = useAtom($selectedStatus);
  const [selectedSorting, setSelectedSorting] = useAtom($currentSorting);
  const [offset, setOffset] = useAtom($offset);
  const [limit, setLimit] = useAtom($limit);
  const [isCreateFormOpen, setIsCreateFormOpen] = React.useState(false);

  const debouncedSearchTerm = useDebouncedValue(searchTerm ?? '', 500);

  const {
    data: { assets = [], total = 0 } = {},
    error,
    isLoading,
    refetch,
  } = useAssetsQuery({
    searchTerm: debouncedSearchTerm,
    status: selectedStatus ? [selectedStatus] : [], // TODO replace by multiselect
    limit,
    offset,
    sorting: selectedSorting ? [selectedSorting] : [],
  });

  const handlePaginationChange: PaginationProps['onChange'] = ({
    limit: newLimit,
    offset: newOffset,
  }) => {
    setLimit(newLimit);
    setOffset(newOffset);
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value || undefined);
    setOffset(0);
  };

  const handleStatusChange = (key: string | undefined) => {
    setSelectedStatus(key === 'all' || !key ? undefined : (key as AssetStatus));
    setOffset(0);
  };

  const getAssetStatusColor = (status: AssetStatus): BadgeProps['color'] => {
    switch (status) {
      case enumAssetStatus.Deployed:
        return 'olive';
      case enumAssetStatus.InTransit:
        return 'teal';
      case enumAssetStatus.Inventory:
        return 'purple';
      case enumAssetStatus.OutOfOrder:
        return 'failure';
      default:
        return 'neutral';
    }
  };

  const handleRowDoubleClick = ({ row }: { row: AssetRecord }) =>
    setLocation(`/transportation/assets/${row.id}`);

  const ASSET_COLUMNS: DataGridColumn<AssetRecord>[] = [
    {
      key: 'identifier',
      header: t.assetsView.form.fields.identifier,
      icon: HeaderIcons.Text,
      cell: ({ row: record }) => record.identifier,
      sortable: true,
      onDoubleClick: handleRowDoubleClick,
    },
    {
      key: 'type',
      header: t.assetsView.form.fields.type,
      icon: HeaderIcons.Text,
      cell: ({ row: record }) => {
        if (!record.type) return null;
        return <Badge size="small">{record.type?.name}</Badge>;
      },
      sortable: true,
      onDoubleClick: handleRowDoubleClick,
    },
    {
      key: 'accountName',
      header: 'Account',
      icon: HeaderIcons.Text,
      resizable: true,
      sortable: true,
      grow: 1,
      cell: ({ row: record }) =>
        record.status === enumAssetStatus.Deployed
          ? (record.account?.name ?? '-')
          : '-',
      onDoubleClick: handleRowDoubleClick,
    },
    {
      key: 'location',
      header: 'Location',
      icon: HeaderIcons.Address,
      resizable: true,
      grow: 1,
      cell: ({ row: record }) => {
        if (record.status === enumAssetStatus.Deployed) {
          return record.accountAddress?.name ?? '-';
        }
        if (record.status === enumAssetStatus.Inventory) {
          return record.facility?.facilityName ?? '-';
        }
        return '-';
      },
      onDoubleClick: handleRowDoubleClick,
    },
    {
      key: 'daysOnSite',
      header: 'Days on Site',
      icon: HeaderIcons.Number,
      cell: ({ row: record }) => record.daysOnSite,
      onDoubleClick: handleRowDoubleClick,
    },
    {
      key: 'status',
      header: t.assetsView.form.fields.status,
      icon: HeaderIcons.Tag,
      cell: ({ row: record }) => {
        if (!record.status) return null;
        const color = getAssetStatusColor(record.status);
        return (
          <Badge color={color} size="small">
            {record.status}
          </Badge>
        );
      },
      sortable: true,
      onDoubleClick: handleRowDoubleClick,
    },
    {
      key: 'actions',
      header: '',
      width: 150,
      cell: ({ row: _record }) => <LegacyMenu color="neutral">...</LegacyMenu>,
    },
  ];

  if (error) {
    return (
      <TransportationRootLayout>
        <div className="p-4 text-failure">
          {t.assetsView.content.error(error.message)}
        </div>
      </TransportationRootLayout>
    );
  }

  const handleColumnSort = (
    columnId: string,
    direction: 'ascending' | 'descending' | null,
  ): void => {
    if (!direction) {
      // Reset sorting - you might want to set a default sort or clear it
      setSelectedSorting(undefined);
      return;
    }

    switch (columnId) {
      case 'identifier':
        setSelectedSorting(
          direction === 'ascending'
            ? enumAssetSortOption.IdentifierAsc
            : enumAssetSortOption.IdentifierDesc,
        );
        break;
      case 'type':
        setSelectedSorting(
          direction === 'ascending'
            ? enumAssetSortOption.StatusAsc
            : enumAssetSortOption.StatusDesc,
        );
        break;
    }
  };

  return (
    <TransportationRootLayout className="@AssetsListView">
      <Toolbar>
        <div className="flex items-center gap-4">
          <SearchField
            aria-label={t.assetsView.toolbar.search.label}
            className="w-80"
            placeholder={t.assetsView.toolbar.search.placeholder}
            value={searchTerm ?? ''}
            onChange={handleSearch}
            showClearButton
          />
          <SelectEnum<AssetStatus>
            enumValue={enumAssetStatus}
            showAllOption
            allOptionLabel={t.assetsView.filters.status.all}
            value={selectedStatus ?? 'all'}
            onChange={handleStatusChange}
            className="w-40"
          />
          <Button
            onPress={() => setIsCreateFormOpen(true)}
            variant="solid"
            color="primary"
            icon={<PlusCircle />}
          >
            {t.assetsView.toolbar.addAsset}
          </Button>
        </div>
      </Toolbar>

      {selectedRows.length > 0 && (
        <FloatingToolbar visible={selectedRows.length > 0}>
          <span className="text-sm">
            <Badge color="primary" size="small">
              {selectedRows.length}
            </Badge>{' '}
            selected
          </span>
          <Separator hideDivider />
          <Button
            variant="solid"
            color="failure"
            icon={<Trash />}
            isDisabled={selectedRows.length < 1}
            onPress={() => console.log('Delete selected assets')}
          >
            Delete selected
          </Button>
        </FloatingToolbar>
      )}

      <Content>
        <Loading isLoading={isLoading}>
          <DataGrid<AssetRecord>
            columns={ASSET_COLUMNS}
            dataSource={assets}
            rowKey="id"
            aria-label={t.assetsView.content.availableAssets}
            selectionMode="multiple"
            onSelectionChange={(keys) => {
              if (keys === 'all') {
                setSelectedRows(assets);
              } else {
                const selectedAssets = assets.filter((asset) =>
                  keys.has(String(asset.id)),
                );
                setSelectedRows(selectedAssets);
              }
            }}
            onColumnSort={handleColumnSort}
            fullWidth
          />
        </Loading>
      </Content>

      <Footer>
        <Pagination
          limit={limit}
          offset={offset}
          total={total}
          onChange={handlePaginationChange}
        />
      </Footer>

      <AssetDialog
        isOpen={isCreateFormOpen}
        onClose={() => setIsCreateFormOpen(false)}
        onSuccess={() => {
          setIsCreateFormOpen(false);
          refetch();
        }}
      />
    </TransportationRootLayout>
  );
};
